import React, { useState, useEffect, useContext, useCallback } from "react";
import GlobalContext from "contexts/GlobalContext";
import Page from "pages/Page";
import {
  AppContent,
  Content,
  Link,
  Pagination,
  AddAlt32,
  Form,
  TextInput,
  ValueAppProjectList,
  Modal,
  useNavigator,
  paginator,
  isGoogleBot,
  usePathFactory,
  useTranslator,
} from "@sia/style-guide";
import i18n from "i18next";
import KeycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import { Link as RouterLink } from "react-router-dom";
import "./ValueApp.scss";

const ValueApp = ({ isSubscribedToValueApp }) => {
  const translate = useTranslator();
  const navigate = useNavigator();
  const { language, user } = useContext(GlobalContext);
  const pathFactory = usePathFactory();
  const [page, setPage] = useState(1);
  const [projectPreviewsItems, setProjectPreviewsItems] = useState([]);
  const [paginatedItems, setPaginatedItems] = useState([]);
  const [perPageLimit, setPerPageLimit] = useState(10);
  const userAgent = navigator.userAgent;
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [projectTitle, setProjectTitle] = useState("");
  const TERMS_URL = process.env.REACT_APP_VALUE_APP_TERMS_URL;

  const refreshProjects = useCallback(() => {
    pgAdapter.getValueAppProjectList(
      (response) => {
        const { data } = response;
        // Map API data to the preview items that include per-project actions.
        const items = data.map((project) => ({
          id: project.id,
          title: project.title,
          status: project.status.name,
          project_id: project.project_id,
          user_id: project.user_id,
          url: `manage/widget/${project.project_id}`,
          translate: translate,
          actions: {
            change: () => navigate(`value-app/manage/widget/${project.project_id}`),
            remove: () => {
              pgAdapter.closeValueAppProject(
                { project_id: project.project_id },
                (response) => {
                  refreshProjects();
                },
                (error) => {
                  console.error("Error closing project", error);
                }
              );
            },
          },
        }));
        setProjectPreviewsItems(items);
      },
      (error) => {
        console.error("Error retrieving project list:", error);
      }
    );
  }, [navigate, translate]);

  useEffect(() => {
    if (user && isSubscribedToValueApp) {
      refreshProjects();
    }
  }, [user, isSubscribedToValueApp, refreshProjects]);

  const onClickSubscribe = () => {
    navigate("account/services");
  };

  const onPaginate = (targetPage, perPageLimit) => {
    setPage(targetPage);
    setPerPageLimit(perPageLimit);
    setPaginatedItems(paginator.paginateItems(projectPreviewsItems, targetPage, perPageLimit));
  };

  useEffect(() => {
    setPage(1);
    if (isGoogleBot(userAgent)) {
      setPaginatedItems(paginator.paginateItems(projectPreviewsItems, 1, projectPreviewsItems.length));
    } else {
      setPaginatedItems(paginator.paginateItems(projectPreviewsItems, 1, perPageLimit));
    }
  }, [projectPreviewsItems, perPageLimit, userAgent]);

  const renderLoginLink = () => {
    const afterLoginRedirectUrl = window.location.origin + pathFactory.create("value-app");
    return (
      <Link
        withLeadIcon
        /**
         * Using callback target instead of string, otherwise it causes:
         * - error in keycloak-js, probably lifecycle/timing problem
         * - infinite reload
         */
        target={() => (window.location.href = KeycloakIDPAdapter.createLoginUrl(i18n.language, afterLoginRedirectUrl))}
      >
        {translate("value-app.login")}
      </Link>
    );
  };

  const handleSaveProject = () => {
    const projectData = {
      title: projectTitle,
    };

    pgAdapter.addValueAppProject(
      projectData,
      (response) => {
        refreshProjects();
      },

      (error) => {
        console.error("Error adding project:", error);
      }
    );
    setProjectTitle("");
    setShowProjectForm(false);
  };

  const renderProjectFormButton = (
    <Content width={"full"}>
      <div className="ValueAppProject-Addbtn">
        <AddAlt32
          onClick={() => {
            setShowDisclaimerModal(showProjectForm ? false : true);
            setShowProjectForm(false);
          }}
        />
      </div>

      {showDisclaimerModal && (
        <Modal
          title={translate("value-app.disclaimer.heading")}
          lead={
            <>
              <p>{translate("value-app.disclaimer.message1")}</p>
              <p>{translate("value-app.disclaimer.message2")}</p>
            </>
          }
          confirmLabel={translate("modal.button.confirm")}
          cancelLabel={translate("modal.button.cancel")}
          onConfirm={() => {
            setShowDisclaimerModal(false);
            setShowProjectForm(true);
          }}
          onCancel={() => {
            setShowDisclaimerModal(false);
          }}
        />
      )}

      {showProjectForm && (
        <div className="ValueAppProjectForm">
          <Form primaryButtonLabel={translate("value-app.project.save")} onClickPrimaryButton={handleSaveProject}>
            <TextInput
              id="projectTitle"
              label={translate("value-app.project.title")}
              required
              value={projectTitle}
              onChange={(event, value) => setProjectTitle(value)}
            />
          </Form>
        </div>
      )}
    </Content>
  );

  return (
    language && (
      <Page title={translate("header.app-switcher.value-app.name")}>
        <Content width={"full"}>
          <AppContent>
            <p>{translate("value-app.description")}</p>
            {user
              ? !isSubscribedToValueApp && (
                  <>
                    <p>{translate("value-app.no-access")}</p>
                    <Link target={onClickSubscribe} withLeadIcon>
                      {translate("value-app.subscribe")}
                    </Link>
                  </>
                )
              : renderLoginLink()}
            <div className="jobs-link">
              <Link target={`/${language}/${TERMS_URL}`} withLeadIcon>
                {translate("value-app.terms")}
              </Link>
            </div>
          </AppContent>
          {user && isSubscribedToValueApp && (
            <>
              {projectPreviewsItems.length > 0 ? (
                <>
                  <ValueAppProjectList items={paginatedItems} elementToRender={RouterLink} />
                  {renderProjectFormButton}
                  {projectPreviewsItems.length > 10 && (
                    <Content width={"full"}>
                      {isGoogleBot(userAgent) ? null : (
                        <Pagination items={projectPreviewsItems} onChange={onPaginate} translate={translate} page={page} />
                      )}
                    </Content>
                  )}
                </>
              ) : (
                <Content>
                  <div>
                    <p>
                      {translate("value-app.no-projects")}
                      {renderProjectFormButton}
                    </p>
                  </div>
                </Content>
              )}
            </>
          )}
        </Content>
      </Page>
    )
  );
};

export default ValueApp;
